import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, Building2, KeyRound, Sparkles, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";

const servicesJsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "DH Cleaning Services",
  itemListElement: [
    {
      "@type": "ListItem", position: 1, item: {
        "@type": "Service", name: "Residential Cleaning",
        description: "Keep your home fresh and inviting with our thorough residential cleaning service.",
        provider: { "@type": "LocalBusiness", name: "DH Cleaning Services" },
        areaServed: { "@type": "City", name: "Melbourne" },
      },
    },
    {
      "@type": "ListItem", position: 2, item: {
        "@type": "Service", name: "Commercial Cleaning",
        description: "Reliable, after-hours commercial cleaning for offices, retail, and more.",
        provider: { "@type": "LocalBusiness", name: "DH Cleaning Services" },
        areaServed: { "@type": "City", name: "Melbourne" },
      },
    },
    {
      "@type": "ListItem", position: 3, item: {
        "@type": "Service", name: "End of Lease Cleaning",
        description: "Comprehensive bond clean to help you get your full deposit back.",
        provider: { "@type": "LocalBusiness", name: "DH Cleaning Services" },
        areaServed: { "@type": "City", name: "Melbourne" },
      },
    },
    {
      "@type": "ListItem", position: 4, item: {
        "@type": "Service", name: "Carpet & Window Cleaning",
        description: "Professional carpet and window cleaning using professional-grade equipment.",
        provider: { "@type": "LocalBusiness", name: "DH Cleaning Services" },
        areaServed: { "@type": "City", name: "Melbourne" },
      },
    },
  ],
};

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const services = [
  {
    icon: Home,
    title: "Residential Cleaning",
    desc: "Keep your home fresh and inviting with our thorough residential cleaning service. We handle everything from dusting and vacuuming to kitchens and bathrooms.",
    includes: ["Dusting & wiping all surfaces", "Vacuuming & mopping floors", "Kitchen & bathroom deep clean", "Bed making & tidying up", "Trash removal"],
  },
  {
    icon: Building2,
    title: "Commercial Cleaning",
    desc: "A clean workplace boosts productivity and impresses clients. We provide reliable, after-hours commercial cleaning for offices, retail, and more.",
    includes: ["Office & desk sanitisation", "Restroom deep cleaning", "Floor care & vacuuming", "Kitchen & breakroom cleaning", "Waste management"],
  },
  {
    icon: KeyRound,
    title: "End of Lease Cleaning",
    desc: "Moving out? Our comprehensive bond clean ensures your property passes the final inspection and you get your full deposit back.",
    includes: ["Full property deep clean", "Oven & rangehood degreasing", "Window tracks & sills", "Wall spot cleaning", "Carpet steam clean (add-on)"],
  },
  {
    icon: Sparkles,
    title: "Carpet & Window Cleaning",
    desc: "Revive your carpets and let the light shine in with our specialist carpet and window cleaning services using professional-grade equipment.",
    includes: ["Hot water carpet extraction", "Stain & odour treatment", "Interior & exterior windows", "Screen & frame wipe-down", "Streak-free finish guarantee"],
  },
];

const Services = () => {
  return (
    <Layout>
      <SEO
        title="Our Cleaning Services Melbourne"
        description="Residential, commercial, end of lease, carpet & window cleaning in Melbourne. Fully insured, eco-friendly products. View what's included and book online."
        canonical="/services"
        keywords="residential cleaning Melbourne, commercial cleaning Melbourne, end of lease cleaning, carpet cleaning, window cleaning, bond cleaning Melbourne"
        jsonLd={servicesJsonLd}
      />
      <section className="bg-gradient-to-br from-primary/10 via-background to-accent/5 py-16 lg:py-20">
        <div className="container text-center">
          <motion.div {...fadeUp}>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-foreground mb-4">Our Cleaning Services</h1>
            <p className="text-lg text-muted-foreground max-w-lg mx-auto">
              From everyday home cleans to specialist deep cleans — we have the right solution for you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Cards */}
      <section className="py-16 lg:py-24">
        <div className="container space-y-12">
          {services.map((s, i) => (
            <motion.div key={s.title} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }}>
              <Card className="overflow-hidden border-2 border-transparent hover:border-primary/10 transition-colors">
                <CardContent className="p-0">
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
                    <div className="lg:col-span-1 bg-primary/5 flex items-center justify-center py-10 lg:py-0">
                      <div className="h-20 w-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                        <s.icon className="h-10 w-10 text-primary" aria-hidden="true" />
                      </div>
                    </div>
                    <div className="lg:col-span-4 p-6 lg:p-8">
                      <h2 className="font-display text-2xl font-bold text-foreground mb-3">{s.title}</h2>
                      <p className="text-muted-foreground mb-5">{s.desc}</p>
                      <h3 className="font-display font-semibold text-sm text-foreground mb-3">What's Included:</h3>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                        {s.includes.map((item) => (
                          <li key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <CheckCircle2 className="h-4 w-4 text-primary shrink-0" aria-hidden="true" /> {item}
                          </li>
                        ))}
                      </ul>
                      <Button asChild>
                        <Link to="/booking">Book Now <ArrowRight className="ml-1 h-4 w-4" /></Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>
    </Layout>
  );
};

export default Services;
